import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { SectionCard } from '@/components/shared/section-card'
import { EmptyState } from '@/components/shared/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Users } from 'lucide-react'
import type { AnalyticsData } from '@/lib/api'
import { fmtCompact, monthLabel } from './utils'

export function CohortSection({
  cohorts,
  isLoading,
}: {
  cohorts: AnalyticsData['cohorts']
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (cohorts.length === 0) {
    return (
      <SectionCard title="Cohort: Retenção de Clientes Indicados">
        <EmptyState
          icon={Users}
          title="Sem dados de cohort ainda"
          description="Recompras aparecerão aqui quando clientes indicados comprarem novamente."
        />
      </SectionCard>
    )
  }

  // Find max revenue for proportional bars
  const allRevenues = cohorts.flatMap((c) => c.months.map((m) => m.revenue))
  const maxRevenue = Math.max(...allRevenues, 1)

  // Find max month offset across all cohorts
  const maxOffset = Math.max(...cohorts.flatMap((c) => c.months.map((m) => m.monthOffset)), 0)
  const offsets = Array.from({ length: Math.min(maxOffset + 1, 12) }, (_, i) => i)

  return (
    <SectionCard
      title="Cohort: Retenção de Clientes Indicados"
      description="Receita acumulada por mês desde a entrada da safra"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">
            Cohort de retenção de clientes indicados — receita por mês desde a entrada
          </caption>
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Safra
              </th>
              <th className="text-center py-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Qtd
              </th>
              {offsets.map((i) => (
                <th
                  key={i}
                  className="text-center py-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground min-w-[80px]"
                >
                  Mês {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map((cohort) => {
              const monthMap = new Map(cohort.months.map((m) => [m.monthOffset, m.revenue]))
              return (
                <tr
                  key={cohort.cohortMonth}
                  className="border-b border-border/50 hover:bg-muted/50"
                >
                  <td className="py-2 pr-4 font-medium whitespace-nowrap">
                    {monthLabel(cohort.cohortMonth)} {cohort.cohortMonth.split('-')[0]}
                  </td>
                  <td className="text-center py-2 px-2 text-muted-foreground">
                    {cohort.totalReferrals}
                  </td>
                  {offsets.map((offset) => {
                    const revenue = monthMap.get(offset)
                    if (revenue === undefined) {
                      return <td key={offset} className="py-2 px-2" />
                    }
                    const width = Math.max((revenue / maxRevenue) * 100, 8)
                    const opacity = 0.2 + (revenue / maxRevenue) * 0.8
                    return (
                      <td key={offset} className="py-2 px-2">
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium">{fmtCompact(revenue)}</span>
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${width}%`, opacity }}
                            />
                          </div>
                        </div>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}
